import { createAsyncThunk } from "@reduxjs/toolkit";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { GlobalService, HttpService, parseApiErrors } from "../../modules";
import i18n from "../../i18n";

// Types for action parameters
export interface RegionSettingsParams {
    page?: number;
    limit?: number;
    isShowLoader?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    status?: string;
    currency?: string;
    language?: string;
}

export interface CreateRegionSettingsData {
    supportedLanguages: Array<{ id: string }>;
    serviceAreas: Array<{ id: string }>;
    quoteCurrency: { id: string };
    defaultCurrency: { id: string };
    defaultLanguage: { id: string };
    supportPhone: string;
    supportEmail: string;
    businessWeekendEndHour: string;
    businessWeekendStartHour: string;
    businessWeekdayEndHour: string;
    businessWeekdayStartHour: string;
    commissionEmergencyServiceHoliday: number;
    commissionEmergencyServiceWeekend: number;
    commissionEmergencyServiceNight: number;
    commissionEmergencyServiceDay: number;
    commissionRegularService: number;
    quoteAmount: number;
    code: string;
    name: string;
}

export interface UpdateRegionSettingsData extends CreateRegionSettingsData {
    id: string;
}

// Get All Region Settings
export const getAllRegionSettings = createAsyncThunk<any, RegionSettingsParams>(
    "regionSettings/getAllRegionSettings",
    async ({ 
        page = 1, 
        limit = 10, 
        isShowLoader = true, 
        search, 
        sortBy = "createdAt", 
        sortOrder = "desc",
        status,
        currency,
        language
    }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());
            params.append("sortBy", sortBy);
            params.append("sortOrder", sortOrder);
            
            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }
            
            if (status && status !== "all") {
                params.append("status", status);
            }
            
            if (currency && currency !== "all") {
                params.append("currency", currency);
            }
            
            if (language && language !== "all") {
                params.append("language", language);
            }
            
            const response = await HttpService.get(
                `api/v1/region-settings?${params.toString()}`,
                true,
                {}
            );
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data || response,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Region Settings failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Create Region Settings
export const createRegionSettings = createAsyncThunk<any, { data: CreateRegionSettingsData }>(
    "regionSettings/createRegionSettings",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/region-settings",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.regionSettings.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Update Region Settings
export const updateRegionSettings = createAsyncThunk<any, { id: string; data: CreateRegionSettingsData }>(
    "regionSettings/updateRegionSettings",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.patch(
                `api/v1/region-settings/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.regionSettings.updated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Delete Region Settings
export const deleteRegionSettings = createAsyncThunk<any, { id: string }>(
    "regionSettings/deleteRegionSettings",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/region-settings/${id}`,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.regionSettings.deleted"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Region Settings deletion failed";

            console.log(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get Region Settings by ID
export const getRegionSettingsById = createAsyncThunk<any, { id: string }>(
    "regionSettings/getRegionSettingsById",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/region-settings/${id}`,
                true,
                {}
            );

            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Region Settings failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
